import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';

/**
 * Serial-number field with a searchable list of rental assets (copied from
 * NetSuite). Typing still works for a machine that is not in the list; picking
 * one fills the serial and the machine name. Assets already on another live
 * contract are shown but cannot be picked.
 */
const SerialSelect = ({ orderId, value, onChange, onPick, disabled, refreshKey }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      axios.get(`${API_URL}/rental/assets`, { params: { q: value || '', limit: 30, order_id: orderId } })
        .then(resp => { if (!cancelled) setItems(resp.data?.items || []); })
        .catch(() => { if (!cancelled) setItems([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, value, orderId, refreshKey]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (item) => {
    if (item.in_use_by || !item.serial_number) return;
    onPick(item);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(i => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && items[active]) { e.preventDefault(); choose(items[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="rs-combo" ref={wrapRef}>
      <input
        className="rs-input"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        placeholder="ค้นหา Serial / ชื่อเครื่อง"
        value={value}
        disabled={disabled}
        onFocus={() => { setOpen(true); setActive(0); }}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
      />
      {open && !disabled && (
        <div className="rs-options rs-combo__list rs-serials" role="listbox">
          {loading && items.length === 0 && <div className="rs-options__empty">กำลังค้นหา...</div>}
          {!loading && items.length === 0 && <div className="rs-options__empty">ไม่พบเครื่องในรายการ — พิมพ์ Serial เองได้</div>}
          {items.map((item, i) => {
            const blocked = Boolean(item.in_use_by) || !item.serial_number;
            return (
              <button
                type="button"
                role="option"
                aria-selected={item.serial_number === value}
                aria-disabled={blocked}
                key={item.asset_number}
                className={`rs-option${i === active ? ' is-active' : ''}${blocked ? ' is-disabled' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(item)}
              >
                <span className="rs-option__title">{item.serial_number || 'ไม่มี Serial'} · {item.asset_name}</span>
                <span className="rs-option__meta">
                  {item.asset_number}
                  {item.in_use_by ? ` · ใช้อยู่ในสัญญา ${item.in_use_by}` : (!item.serial_number ? ' · ยังไม่มี Serial ใน NetSuite' : '')}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SerialSelect;
